import React from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Icon } from '@cotera/client/app/components/ui/icon';
import type { DataGridColumn } from '../types';

/** Above this many columns the list is worth narrowing rather than scrolling. */
const FILTER_THRESHOLD = 12;

const labelFor = <TRow,>(column: DataGridColumn<TRow>): string =>
  typeof column.header === 'string' ? column.header : column.id;

/**
 * One chip standing in for every hidden column, opening the panel that manages
 * which columns are shown.
 *
 * Replaces a chip per hidden column. That scaled badly in the obvious way — a
 * dataset with thirty columns and most of them hidden filled the toolbar with
 * pills and pushed everything else off it — but the worse problem was that it
 * split one idea across two places: columns were hidden from the column menu
 * and restored from the toolbar, so there was nowhere to see visibility as a
 * whole. This panel is that place, and the chip is a count rather than a list,
 * so the toolbar costs the same whether one column is hidden or forty.
 *
 * Only rendered while something is hidden. With nothing hidden there is no
 * state to report and the column menu is already where hiding starts, so a
 * permanent control would be a third entry point for a thing nobody is looking
 * for yet.
 */
export function DataGridHiddenColumnsChip<TRow>({
  columns,
  setColumnVisible,
}: {
  columns: DataGridColumn<TRow>[];
  setColumnVisible: (columnId: string, visible: boolean) => void;
}) {
  const [search, setSearch] = React.useState('');

  const hidden = columns.filter((column) => column.visible === false);
  const query = search.trim().toLowerCase();
  const listed =
    query === ''
      ? columns
      : columns.filter((column) =>
          labelFor(column).toLowerCase().includes(query)
        );

  if (hidden.length === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          // The name is spelled out for a reader who cannot see the icon; the
          // face of the chip is not, because this sits in a toolbar that has to
          // survive a narrow pane and "1 hidden column" is three times the width
          // of what it adds over "1 hidden" next to an eye-slash.
          aria-label={`${hidden.length} hidden ${
            hidden.length === 1 ? 'column' : 'columns'
          }`}
          className="flex min-w-0 shrink items-center gap-1 rounded-full border border-border bg-muted/50 py-0.5 pl-2 pr-2 text-[11px] transition-colors hover:bg-muted hover:text-foreground"
        >
          <Icon icon="eye-slash" size="small" className="shrink-0 opacity-60" />
          {/* Truncates rather than being clipped by whatever contains it. The
            toolbar cut this mid-word at the pane's default width, which reads
            as a broken control rather than as a full one. */}
          <span className="truncate tabular-nums">{hidden.length} hidden</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // normal-case: the toolbar this opens from is uppercase, and a panel of
        // column names inherited it — shouting back every column's name.
        className="w-72 p-0 normal-case tracking-normal"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <span className="text-xs font-medium text-foreground">Columns</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() =>
                columns.forEach((column) => setColumnVisible(column.id, true))
              }
            >
              Show all
            </button>
          </div>
        </div>
        {columns.length > FILTER_THRESHOLD ? (
          <div className="border-b border-border px-2 py-2">
            {/* Narrows this list; it does not search the dataset. The toolbar
              has one control called search, and a second thing wearing that
              name a click away taught readers to expect a search to sometimes
              only look at what is in front of it. Naming it for what it does
              leaves the word "search" meaning one thing. */}
            <Input
              aria-label="Filter this list of columns"
              placeholder="Filter this list…"
              className="h-7 text-xs"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        ) : null}
        <div className="max-h-72 overflow-y-auto p-1">
          {listed.length === 0 ? (
            <p className="px-2 py-3 text-center text-[11px] text-muted-foreground">
              No column in this list is named “{search.trim()}”.
            </p>
          ) : (
            listed.map((column) => {
              const visible = column.visible !== false;
              return (
                <label
                  key={column.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-muted/60"
                >
                  <Checkbox
                    checked={visible}
                    aria-label={labelFor(column)}
                    onCheckedChange={() =>
                      setColumnVisible(column.id, !visible)
                    }
                  />
                  <span
                    className={`min-w-0 flex-1 truncate text-xs ${
                      visible ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {labelFor(column)}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
